import { z } from 'zod';
import { coreIdSchema } from './ids.js';

/**
 * Per-organization enterprise identity-provider binding.
 *
 * The IdP binding is data, not deployment configuration: the
 * `organization_idp_configs` row is the runtime source of truth, and env
 * only seeds it. See docs/internal/product/enterprise-identity.md.
 */

/**
 * Maps an IdP group (by display name) onto a Reflex role and/or team.
 * Groups listed here are "managed": membership in the group grants the
 * binding, and leaving the group revokes it. Roles and teams outside
 * this list are never touched by provisioning.
 */
export const IdpGroupMappingSchema = z.object({
  group: z.string().min(1),
  roleId: z.string().min(1).optional(),
  teamId: z.string().min(1).optional(),
});
export type IdpGroupMapping = z.infer<typeof IdpGroupMappingSchema>;

/**
 * Built-in organization roles an IdP group may never confer, by slug.
 *
 * Whoever holds the SCIM bearer (or the Okta API token) can assert any group
 * for any user, so every entry here is a role that must not be reachable from
 * that credential — or, for `member`, one that provisioning would break rather
 * than grant:
 *
 * - `owner` — billing and deletion rights; a mapping turns a leaked
 *   provisioning credential into an org takeover.
 * - `company-manager` — holds `company:manage`, which administers this very
 *   configuration, including the SCIM token that would be asserting the group.
 *   It also requires an auditable actor that unattended provisioning has none
 *   of, so the assignment could only ever fail.
 * - `member` — the baseline role `addMember` grants to every member. Mapping
 *   it means a user outside the group has their base role revoked moments
 *   after joining, leaving a membership with no permissions at all.
 *
 * `admin` is deliberately absent: mapping it is a real thing an administrator
 * may want, and the UI says what it implies.
 *
 * Shared because the server refuses these at write time and the settings UI
 * must not offer what the server will refuse. One list, both sides.
 */
export const NON_MAPPABLE_BUILTIN_ROLE_SLUGS = ['owner', 'company-manager', 'member'] as const;

/**
 * Whether a role may be named by an IdP group mapping. Only built-in roles are
 * ever refused — a custom org role is the administrator's own construction.
 */
export function isMappableIdpRole(role: { slug: string; isBuiltin: boolean }): boolean {
  return !(
    role.isBuiltin && (NON_MAPPABLE_BUILTIN_ROLE_SLUGS as readonly string[]).includes(role.slug)
  );
}

/**
 * How many client IDs an org may allowlist for ID-JAG. Far above any real
 * deployment (Claude, Cursor, VS Code, a couple of internal apps) and low
 * enough that the list stays something a human reviews.
 */
export const MAX_ID_JAG_ALLOWED_CLIENT_IDS = 50;

/**
 * How many IdP group mappings an org may hold. Generous for a real directory
 * (a few dozen groups is a large tenant) and small enough that the mapping
 * table stays something an admin reads top to bottom.
 */
export const MAX_IDP_GROUP_MAPPINGS = 200;

export const IdpConfigViewSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  ssoEnabled: z.boolean(),
  jitEnabled: z.boolean(),
  requireSso: z.boolean(),
  displayName: z.string().nullable(),
  issuer: z.string(),
  /**
   * Whether this org accepts ID-JAG assertions signed by {@link issuer} in
   * place of the browser consent flow. See
   * docs/internal/plans/id-jag-cross-app-access.md.
   */
  idJagEnabled: z.boolean(),
  /** Empty means "any client this issuer vouches for". */
  idJagAllowedClientIds: z.array(z.string()),
  /** NULL resolves the JWKS from the issuer's discovery document. */
  jwksUri: z.string().nullable(),
  groupMappings: z.array(IdpGroupMappingSchema),
  oktaOrgUrl: z.string().nullable(),
  oktaSyncEnabled: z.boolean(),
  /** Whether a SCIM bearer token has been issued; the token itself is never returned. */
  scimTokenSet: z.boolean(),
  lastSyncedAt: z.number().nullable(),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type IdpConfigView = z.infer<typeof IdpConfigViewSchema>;

/**
 * Platform-admin view of an org's enterprise identity state. Entitlement
 * follows the org's subscription tier (see `SUBSCRIPTION_TIER_FEATURES`);
 * this view exists so an operator changing the tier can see what a
 * downgrade would stop.
 */
export const AdminEnterpriseIdentityStatusSchema = z
  .object({
    entitled: z.boolean(),
    setupState: z.enum(['not-entitled', 'not-configured', 'configured']),
    configuredFeatures: z.object({
      identityProvider: z.boolean(),
      scim: z.boolean(),
      oktaSync: z.boolean(),
      idJag: z.boolean(),
    }),
  })
  .meta({ id: 'AdminEnterpriseIdentityStatus' });
export type AdminEnterpriseIdentityStatus = z.infer<typeof AdminEnterpriseIdentityStatusSchema>;

export const UpsertIdpConfigRequestSchema = z.object({
  ssoEnabled: z.boolean().optional(),
  jitEnabled: z.boolean().optional(),
  requireSso: z.boolean().optional(),
  displayName: z.string().min(1).max(200).nullish(),
  issuer: z.string().url(),
  idJagEnabled: z.boolean().default(false),
  idJagAllowedClientIds: z
    .array(z.string().min(1).max(512))
    .max(MAX_ID_JAG_ALLOWED_CLIENT_IDS)
    .default([]),
  jwksUri: z.string().url().nullish(),
  groupMappings: z.array(IdpGroupMappingSchema).max(MAX_IDP_GROUP_MAPPINGS).default([]),
  oktaOrgUrl: z.string().url().nullish(),
  oktaSyncEnabled: z.boolean().default(false),
  /**
   * The `updatedAt` the client last read, for optimistic concurrency.
   *
   * This endpoint is a whole-row upsert, and the row is edited from two
   * different pages — provisioning (SCIM, group mappings, Okta sync) and
   * enterprise authorization (ID-JAG). Each echoes back the fields it does not
   * own, so without this a stale read silently reverts the other page's work:
   * an admin enabling ID-JAG could drop the group mappings a colleague saved a
   * minute earlier, and nothing would report it.
   *
   * Optional so existing callers keep working; when present and stale, the
   * write is refused and the client refetches.
   */
  expectedUpdatedAt: z.number().optional(),
  /** Refuse this create if a configuration row appeared after the client read `null`. */
  expectedAbsent: z.literal(true).optional(),
});
export type UpsertIdpConfigRequest = z.infer<typeof UpsertIdpConfigRequestSchema>;

/**
 * Identity providers the connection form offers.
 *
 * The value is a label and a hint, not a code path: sign-in is plain OIDC
 * discovery plus the authorization-code flow for every entry here, and the
 * only provider-conditional server logic is Entra's login-host and tenant
 * check. A new provider is therefore an entry in this enum and in
 * {@link BILLING_ENTITY_OIDC_PROVIDER_DETAILS}, nothing else.
 */
export const BillingEntityOidcProviderSchema = z.enum([
  'okta',
  'entra',
  'google_workspace',
  'auth0',
  'keycloak',
  'zitadel',
  'other',
]);
export type BillingEntityOidcProvider = z.infer<typeof BillingEntityOidcProviderSchema>;

/** The providers in the order the connection form lists them. */
export const BILLING_ENTITY_OIDC_PROVIDERS = BillingEntityOidcProviderSchema.options;

export type BillingEntityOidcProviderDetails = {
  /** The name an administrator knows the product by. */
  label: string;
  /** An issuer of the shape this provider publishes, for a placeholder. */
  issuerExample: string;
  /** Where to find the issuer in the provider's own console, in one sentence. */
  issuerHint: string;
};

/**
 * Per-provider wording, shared so the connection form, the admin surfaces,
 * and the docs never disagree on what a provider is called or what its
 * issuer looks like. The issuer shapes matter: Keycloak's carries the realm
 * in the path and Zitadel's is a bare origin, so one Okta-shaped example
 * would send an administrator looking for a host that does not exist.
 */
export const BILLING_ENTITY_OIDC_PROVIDER_DETAILS: Record<
  BillingEntityOidcProvider,
  BillingEntityOidcProviderDetails
> = {
  okta: {
    label: 'Okta',
    issuerExample: 'https://acme.okta.com',
    issuerHint:
      'The issuer your authorization server publishes: the Okta URL, or your custom domain if the server is set to use it.',
  },
  entra: {
    label: 'Microsoft Entra',
    issuerExample: 'https://login.microsoftonline.com/<tenant-id>/v2.0',
    issuerHint:
      'The tenant-specific v2.0 issuer. Entra’s common, organizations, and consumers endpoints are refused because they name no tenant.',
  },
  google_workspace: {
    label: 'Google Workspace',
    issuerExample: 'https://accounts.google.com',
    issuerHint:
      'Google’s issuer is https://accounts.google.com. Configure the OAuth application for internal users in your Workspace organization.',
  },
  auth0: {
    label: 'Auth0',
    issuerExample: 'https://acme.us.auth0.com',
    issuerHint:
      'Your Auth0 tenant domain, including its region when present, or the custom domain configured for the application.',
  },
  keycloak: {
    label: 'Keycloak',
    issuerExample: 'https://keycloak.acme.com/realms/acme',
    issuerHint:
      'The realm URL, including the /realms/<realm> path. Keycloak publishes one issuer per realm.',
  },
  zitadel: {
    label: 'Zitadel',
    issuerExample: 'https://acme.zitadel.cloud',
    issuerHint: 'Your instance domain, or the custom domain if you have configured one.',
  },
  other: {
    label: 'Other OpenID Connect',
    issuerExample: 'https://idp.example.com',
    issuerHint:
      'The exact issuer from your provider’s OpenID configuration. The provider must support discovery, the authorization code flow, and client_secret_basic authentication.',
  },
};

/** The display name for a provider value. */
export function billingEntityOidcProviderLabel(provider: BillingEntityOidcProvider): string {
  return BILLING_ENTITY_OIDC_PROVIDER_DETAILS[provider].label;
}

export const ReplaceBillingEntityOidcConnectionRequestSchema = z.object({
  provider: BillingEntityOidcProviderSchema,
  issuer: z.string().url(),
  displayName: z.string().trim().min(1).max(200),
  jwksUri: z.string().url().nullable().optional(),
  clientId: z.string().trim().min(1).max(2048),
  /** Omit to retain the credential, only for a versioned update of the same client. */
  clientSecret: z.string().min(1).max(16_384).optional(),
  expectedVersion: z.number().int().positive().optional(),
  /** Refuse this create if a connection appeared after the client read an empty view. */
  expectedAbsent: z.literal(true).optional(),
});
export type ReplaceBillingEntityOidcConnectionRequest = z.infer<
  typeof ReplaceBillingEntityOidcConnectionRequestSchema
>;

export const AdminReplaceBillingEntityOidcConnectionRequestSchema =
  ReplaceBillingEntityOidcConnectionRequestSchema.extend({
    reason: z.string().trim().min(1).max(2000),
  });
export type AdminReplaceBillingEntityOidcConnectionRequest = z.infer<
  typeof AdminReplaceBillingEntityOidcConnectionRequestSchema
>;

export const AdminDeleteBillingEntityOidcConnectionRequestSchema = z.object({
  reason: z.string().trim().min(1).max(2000),
  expectedVersion: z.number().int().positive().optional(),
});

export const DeleteBillingEntityOidcConnectionRequestSchema = z.object({
  expectedVersion: z.number().int().positive().optional(),
});

export const BillingEntityOidcConnectionSchema = z
  .object({
    configured: z.boolean(),
    /**
     * Whether the company's subscription tier includes enterprise identity.
     * Reading the connection is allowed either way, so this is what tells a
     * client the difference between "not set up yet" and "cannot be set up on
     * this plan": every mutation is refused with
     * `enterprise_identity_not_entitled` while it is false, so a surface that
     * cannot see it offers a form whose every save fails.
     */
    entitled: z.boolean(),
    provider: BillingEntityOidcProviderSchema.nullable(),
    issuer: z.string().nullable(),
    displayName: z.string().nullable(),
    jwksUri: z.string().nullable(),
    clientId: z.string().nullable(),
    credentialsConfigured: z.boolean(),
    companySsoEnabled: z.boolean().nullable(),
    companyRequireSso: z.boolean().nullable(),
    ssoPolicyVersion: z.number().int().nonnegative(),
    companyOrganizations: z.array(
      z.object({
        id: coreIdSchema('organization'),
        name: z.string(),
        slug: z.string(),
        ssoEnabled: z.boolean(),
        requireSso: z.boolean(),
      }),
    ),
    version: z.number().int().positive().nullable(),
    createdAt: z.number().nullable(),
    updatedAt: z.number().nullable(),
    /** The one company organization new SSO users join, or null for invite-only access. */
    autoJoinOrganizationId: coreIdSchema('organization').nullable(),
    /** Organizations eligible to become the company's auto-join destination. */
    organizations: z.array(
      z.object({
        id: coreIdSchema('organization'),
        slug: z.string(),
        name: z.string(),
      }),
    ),
  })
  .meta({ id: 'BillingEntityOidcConnection' });
export type BillingEntityOidcConnection = z.infer<typeof BillingEntityOidcConnectionSchema>;

/**
 * Where a member stands on the admin-authorized SSO link
 * (`realm_account_link_grants`), as the enrollment dashboard shows it.
 *
 * - `connected` — already holds an identity from the company IdP; nothing to do.
 * - `eligible` — a Company Manager may authorize the link.
 * - `granted` — an unconsumed grant is waiting for the member's next SSO sign-in.
 * - `ineligible` — the guard in `ineligibleReason` refuses this account.
 */
export const SsoLinkGrantStateSchema = z.enum(['connected', 'eligible', 'granted', 'ineligible']);
export type SsoLinkGrantState = z.infer<typeof SsoLinkGrantStateSchema>;

/**
 * Why a member may not be granted an SSO link. Each is a distinct guard in
 * `RealmAccountLinkGrantService`; the client renders them, so the set is
 * closed here rather than being free text.
 *
 * - `external_membership` — belongs to an organization outside this company,
 *   so a link would hand this company's IdP a session into another tenant.
 * - `unclaimed_domain` — the account's email domain is not one the company
 *   claims.
 * - `platform_admin` — platform admins are the IdP-down recovery path and
 *   never take a realm identity.
 */
export const SsoLinkGrantIneligibleReasonSchema = z.enum([
  'external_membership',
  'unclaimed_domain',
  'platform_admin',
]);
export type SsoLinkGrantIneligibleReason = z.infer<typeof SsoLinkGrantIneligibleReasonSchema>;

export const SsoEnrollmentMemberSchema = z.object({
  userId: coreIdSchema('user'),
  name: z.string().nullable(),
  email: z.string(),
  connected: z.boolean(),
  linkGrant: SsoLinkGrantStateSchema,
  ineligibleReason: SsoLinkGrantIneligibleReasonSchema.nullable(),
});

export const SsoEnrollmentStatusSchema = z
  .object({
    organization: z.object({
      id: coreIdSchema('organization'),
      name: z.string(),
      slug: z.string(),
    }),
    connected: z.number().int().nonnegative(),
    eligible: z.number().int().nonnegative(),
    companyConnected: z.number().int().nonnegative(),
    companyEligible: z.number().int().nonnegative(),
    requireSso: z.boolean(),
    members: z.array(SsoEnrollmentMemberSchema),
  })
  .meta({ id: 'SsoEnrollmentStatus' });
export type SsoEnrollmentStatus = z.infer<typeof SsoEnrollmentStatusSchema>;

/**
 * Authorize the company IdP to attach to existing accounts on their next SSO
 * sign-in. Bulk by design: the case this serves is a rollout, where every
 * member who signed up with a social provider before SSO existed needs the
 * same authorization ahead of `requireSso`.
 */
/**
 * Most userIds one request may carry. The client chunks a larger rollout into
 * batches of this size, so the bound lives here rather than being written
 * twice and drifting.
 */
export const SSO_LINK_GRANT_MAX_BATCH = 500;

export const CreateSsoLinkGrantsRequestSchema = z
  .object({ userIds: z.array(coreIdSchema('user')).min(1).max(SSO_LINK_GRANT_MAX_BATCH) })
  .strict();
export type CreateSsoLinkGrantsRequest = z.infer<typeof CreateSsoLinkGrantsRequestSchema>;

export const SsoLinkGrantResultSchema = z.object({
  userId: coreIdSchema('user'),
  linkGrant: SsoLinkGrantStateSchema,
  ineligibleReason: SsoLinkGrantIneligibleReasonSchema.nullable(),
});
export type SsoLinkGrantResult = z.infer<typeof SsoLinkGrantResultSchema>;

export const CreateSsoLinkGrantsResponseSchema = z
  .object({ results: z.array(SsoLinkGrantResultSchema) })
  .meta({ id: 'CreateSsoLinkGrantsResponse' });
export type CreateSsoLinkGrantsResponse = z.infer<typeof CreateSsoLinkGrantsResponseSchema>;

/**
 * Hosts Okta serves its own multi-tenant clouds from.
 *
 * Advisory, not a trust boundary. A self-managed Okta — and any tenant using
 * Okta's custom-domain feature — issues from a customer-owned host such as
 * `okta.acme.com`, which is a completely ordinary deployment and must not be
 * refused. What makes an issuer trustworthy is that it answers discovery and
 * echoes its own `iss` (see `checkIssuer` on the server), not that its
 * name ends in a string Okta happens to own. This list only powers the
 * "that doesn't look Okta-hosted, is it your custom domain?" hint, so a miss
 * costs a sentence of UI, never a save.
 */
export const OKTA_HOSTED_ISSUER_SUFFIXES = [
  '.okta.com',
  '.oktapreview.com',
  '.okta-emea.com',
  '.okta-gov.com',
  '.okta.mil',
  '.trexcloud.com',
] as const;

/** Whether an issuer URL names one of Okta's own clouds. See the list above. */
export function isOktaHostedIssuer(issuer: string): boolean {
  let hostname: string;
  try {
    hostname = new URL(issuer).hostname.toLowerCase().replace(/\.$/, '');
  } catch {
    return false;
  }
  return OKTA_HOSTED_ISSUER_SUFFIXES.some((suffix) => hostname.endsWith(suffix));
}

/**
 * Why an issuer was refused. The server sends one of these plus a `detail`
 * sentence; the client turns the code into wording an administrator can act
 * on, because "untrusted_oidc_issuer" told nobody anything.
 */
export const OidcIssuerFailureCodeSchema = z.enum([
  /** Not https, an IP literal, or carrying embedded credentials. */
  'invalid_issuer_url',
  /** Entra issuer on a host that is not one of Microsoft's login endpoints. */
  'unsupported_entra_host',
  /** Entra issuer naming no tenant (`common`, `organizations`, `consumers`). */
  'ambiguous_entra_tenant',
  /** JWKS override on a different origin than the issuer. */
  'jwks_origin_mismatch',
  /** Discovery did not answer, or did not answer with a usable document. */
  'discovery_failed',
  /** Discovery answered, but claimed to be a different issuer. */
  'issuer_mismatch',
]);
export type OidcIssuerFailureCode = z.infer<typeof OidcIssuerFailureCodeSchema>;

export const TestBillingEntityOidcConnectionRequestSchema = z.object({
  provider: BillingEntityOidcProviderSchema,
  issuer: z.string().url(),
  jwksUri: z.string().url().nullable().optional(),
});
export type TestBillingEntityOidcConnectionRequest = z.infer<
  typeof TestBillingEntityOidcConnectionRequestSchema
>;

/**
 * The result of probing an issuer, for the connection form's Test button.
 *
 * A failed probe is a 200 with `ok: false`, not an HTTP error: this endpoint
 * answers "what happens if I save this?", and a diagnostic that reports a
 * misconfiguration is doing its job, not failing. The save path runs the
 * identical check, so a green Test means the save will be accepted.
 */
export const BillingEntityOidcIssuerCheckSchema = z
  .object({
    ok: z.boolean(),
    /** The canonicalized issuer the check ran against. */
    issuer: z.string(),
    /** False for a custom or self-managed Okta domain — a hint, never a refusal. */
    oktaHostedDomain: z.boolean(),
    authorizationEndpoint: z.string().nullable(),
    tokenEndpoint: z.string().nullable(),
    /** As advertised by discovery, so an admin can compare it to an override. */
    jwksUri: z.string().nullable(),
    failureCode: OidcIssuerFailureCodeSchema.nullable(),
    /** A sentence written for a person, naming the specific thing that failed. */
    detail: z.string().nullable(),
  })
  .meta({ id: 'BillingEntityOidcIssuerCheck' });
export type BillingEntityOidcIssuerCheck = z.infer<typeof BillingEntityOidcIssuerCheckSchema>;

export const SetBillingEntityAutoJoinRequestSchema = z.object({
  organizationId: coreIdSchema('organization').nullable(),
});
export type SetBillingEntityAutoJoinRequest = z.infer<typeof SetBillingEntityAutoJoinRequestSchema>;

export const RotateScimTokenResponseSchema = z.object({
  /** Plaintext SCIM bearer token — shown exactly once at rotation. */
  token: z.string(),
});
export type RotateScimTokenResponse = z.infer<typeof RotateScimTokenResponseSchema>;

/** Null company policy means legacy organization policies await explicit resolution. */
export const SetCompanySsoPolicyRequestSchema = z
  .object({
    ssoEnabled: z.boolean(),
    requireSso: z.boolean(),
    expectedVersion: z.number().int().nonnegative(),
  })
  .refine((value) => !value.requireSso || value.ssoEnabled, {
    message: 'Enable SSO before requiring it.',
    path: ['requireSso'],
  });
export type SetCompanySsoPolicyRequest = z.infer<typeof SetCompanySsoPolicyRequestSchema>;
